<template>
    <div
        v-if="isOpen"
        class="modal fade show"
        style="display: block; background: #1c1d1c33"
    >
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
                <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                    <h4 class="mb-0">Edit Tax Certificate</h4>
                    <button
                        @click="toggleModal"
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                    ></button>
                </div>
                <hr class="mt-0">
                <div class="px-2 pb-2">
                    <div class="mb-2">
                        <strong>Certificate No:</strong> {{ certificate.certificate_number }}
                    </div>
                    <div v-if="isTds" class="row mb-2">
                        <div class="col-md-6">
                            <strong>Payee:</strong> {{ payee ? payee.full_name : '' }}
                        </div>
                        <div class="col-md-6">
                            <strong>Issued By:</strong> {{ issuer ? issuer.full_name : '' }}
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table v-if="isTds" class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>Supplier Name</th>
                                    <th>TIN</th>
                                    <th>Bill No</th>
                                    <th>Payment Date</th>
                                    <th>Section</th>
                                    <th class="text-right">Base Amount</th>
                                    <th class="text-right">TDS Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(bill, i) in bills" :key="i">
                                    <td>{{ bill.party_name }}</td>
                                    <td>{{ bill.party_tin_no }}</td>
                                    <td>{{ bill.bill_number }}</td>
                                    <td>{{ bill.payment_date }}</td>
                                    <td>{{ bill.tds_code }}</td>
                                    <td class="text-right">{{ commaFormat(bill.base_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.tds_amount) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colspan="5" class="text-right">Total</th>
                                    <th class="text-right">{{ commaFormat(totalBaseAmount) }}</th>
                                    <th class="text-right">{{ commaFormat(totalTdsAmount) }}</th>
                                </tr>
                            </tfoot>
                        </table>
                        <table v-else class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>Supplier Name</th>
                                    <th>BIN</th>
                                    <th>Bill Ref No</th>
                                    <th>Bill Ref Date</th>
                                    <th class="text-right">Total Amount</th>
                                    <th class="text-right">VAT Amount</th>
                                    <th class="text-right">VDS Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(bill, i) in bills" :key="i">
                                    <td>{{ bill.party_name }}</td>
                                    <td>{{ bill.party_bin_no }}</td>
                                    <td>{{ bill.bill_referance_no }}</td>
                                    <td>{{ bill.bill_referance_date }}</td>
                                    <td class="text-right">{{ commaFormat(bill.bill_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.vat_amount) }}</td>
                                    <td class="text-right">{{ commaFormat(bill.vds_amount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!bills.length" class="text-center mt-2">No bills</p>
                    </div>
                    <div class="row mt-2">
                        <div class="mb-2" :class="isTds ? 'col-md-4' : 'col-12'">
                            <label class="form-label">Issue Date</label>
                            <input type="date" class="form-control" v-model="formData.date">
                        </div>
                        <div v-if="isTds" class="col-md-4 mb-2">
                            <label class="form-label">From Date</label>
                            <input type="date" class="form-control" v-model="formData.from_date">
                        </div>
                        <div v-if="isTds" class="col-md-4 mb-2">
                            <label class="form-label">To Date</label>
                            <input type="date" class="form-control" v-model="formData.to_date">
                        </div>
                    </div>
                    <div class="text-center">
                        <hr class="mt-2">
                        <button
                            :disabled="loading"
                            @click="submit"
                            class="btn btn-primary me-1 waves-effect waves-float waves-light"
                        >
                            <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            Update
                        </button>
                        <button
                            :disabled="loading"
                            @click="remove"
                            class="btn btn-danger me-1 waves-effect waves-float waves-light"
                        >
                            <i class="fas fa-trash me-1"></i>
                            Delete
                        </button>
                        <button @click="toggleModal" class="btn btn-outline-secondary waves-effect">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    certificate: {
        type: Object,
        default: () => ({})
    },
    bills: {
        type: Array,
        default: () => []
    },
    payee: {
        type: Object,
        default: null
    },
    issuer: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['onSubmit', 'onDelete']);

const { commaFormat } = figureFormatter();
const isOpen = ref(false);
const loading = ref(false);

const isTds = computed(() => props.certificate.type === 'tds_certificate');

const sumBy = (key) => {
    return props.bills.reduce((total, bill) => total + parseFloat(bill[key] || 0), 0);
}

const totalBaseAmount = computed(() => sumBy('base_amount'));
const totalTdsAmount = computed(() => sumBy('tds_amount'));

const toggleModal = () => {
    isOpen.value = !isOpen.value;
};

const submit = () => {
    emit('onSubmit');
};

const remove = () => {
    emit('onDelete');
};

defineExpose({ toggleModal, loading });
</script>
